<template>
  <div class="min-h-screen bg-[#FAF8F8] px-4 sm:px-6 lg:px-8 py-10">
    <div class="max-w-5xl mx-auto space-y-12">

      <!-- 1. En-tête & Recherche -->
      <div class="text-center space-y-4 max-w-2xl mx-auto">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 border border-purple-200/60 text-xs font-semibold text-[#7C3AED]">
          <span>💡</span> Centre de ressources GiraafFlow
        </div>
        <h1 class="text-3xl sm:text-4xl font-extrabold text-[#0B1B3D] tracking-tight">
          Comment pouvons-nous vous aider ?
        </h1>
        <p class="text-gray-500 text-sm sm:text-base">
          Guides de démarrage, foire aux questions et assistance technique directe pour votre équipe.
        </p>

        <!-- Champ de recherche réactif -->
        <div class="relative mt-6 max-w-lg mx-auto">
          <input v-model="searchQuery" type="text" placeholder="Rechercher une question, un guide, un problème..."
            class="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-2xl shadow-xs focus:border-[#7C3AED] focus:ring-4 focus:ring-[#7C3AED]/10 outline-none transition text-sm text-gray-800" />
          <svg class="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <!-- 2. 4 Cartes de thématiques -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div v-for="cat in categories" :key="cat.title"
          class="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs hover:border-[#7C3AED]/40 hover:shadow-md transition-all duration-200 cursor-pointer group"
          @click="filterByCategory(cat.title)">
          <div class="w-11 h-11 rounded-xl flex items-center justify-center text-xl mb-3" :class="cat.bgClass">
            {{ cat.icon }}
          </div>
          <h3 class="font-bold text-[#0B1B3D] text-sm group-hover:text-[#7C3AED] transition-colors">
            {{ cat.title }}
          </h3>
          <p class="text-xs text-gray-500 mt-1 leading-relaxed">
            {{ cat.desc }}
          </p>
        </div>
      </div>

      <!-- 3. Foire Aux Questions (Accordéon interactif) -->
      <div class="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-xs space-y-6">
        <div class="flex items-center justify-between border-b border-gray-100 pb-4">
          <div>
            <h2 class="text-xl font-bold text-[#0B1B3D]">
              Questions fréquentes (FAQ)
            </h2>
            <p class="text-xs text-gray-500 mt-0.5">
              {{ filteredFaq.length }} réponse(s) disponible(s)
            </p>
          </div>
          <button v-if="searchQuery" @click="searchQuery = ''"
            class="text-xs font-semibold text-[#7C3AED] hover:underline cursor-pointer">
            Réinitialiser
          </button>
        </div>

        <div v-if="filteredFaq.length === 0" class="text-center py-10 text-gray-400 text-sm">
          Aucun résultat trouvé pour « {{ searchQuery }} ».
        </div>

        <div v-else class="space-y-3">
          <div v-for="(item, idx) in filteredFaq" :key="idx"
            class="border border-gray-100 rounded-2xl overflow-hidden transition-all duration-150"
            :class="item.open ? 'bg-purple-50/40 border-purple-200/60' : 'hover:border-gray-200'">
            <button @click="toggleFaq(item)"
              class="w-full flex items-center justify-between p-4 text-left font-semibold text-sm text-[#0B1B3D] gap-4 cursor-pointer">
              <span>{{ item.question }}</span>
              <svg class="w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200"
                :class="item.open ? 'rotate-180 text-[#7C3AED]' : ''" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-if="item.open"
              class="px-4 pb-4 pt-1 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-purple-100/60">
              {{ item.answer }}
            </div>
          </div>
        </div>
      </div>

      <!-- 4. Bannière Support & Contact Direct -->
      <div
        class="bg-[#0B1B3D] rounded-3xl p-6 sm:p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
        <div class="space-y-2 text-center md:text-left">
          <h3 class="text-lg sm:text-xl font-bold">
            Vous ne trouvez pas votre réponse ?
          </h3>
          <p class="text-xs sm:text-sm text-gray-300 max-w-md">
            Notre équipe technique et support vous répond en moins de 2 heures ouvrées.
          </p>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-3">
          <a href="mailto:growth@giraafpark.com"
            style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
            class="text-white text-xs sm:text-sm font-bold px-5 py-3 rounded-xl hover:opacity-95 shadow-sm transition active:scale-[0.98]">
            Contacter le support par e-mail
          </a>
          <a href="tel:+21625838808"
            class="bg-white/10 hover:bg-white/20 text-white border border-white/20 text-xs sm:text-sm font-medium px-4 py-3 rounded-xl transition">
            📞 +216 25 838 808
          </a>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'AideView',
  data() {
    return {
      searchQuery: '',
      categories: [
        {
          title: 'Premiers pas',
          icon: '🚀',
          desc: 'Création de projet, structure des flux et démarrage rapide.',
          bgClass: 'bg-purple-100 text-[#7C3AED]'
        },
        {
          title: 'Gestion & Kanban',
          icon: '📋',
          desc: 'Suivi des échéances, déplacement de lots et statuts en direct.',
          bgClass: 'bg-orange-100 text-[#F97316]'
        },
        {
          title: 'Écosystème & IA',
          icon: '⚡',
          desc: 'Connecter GiraafChat, widgets intelligents et automatisations.',
          bgClass: 'bg-fuchsia-100 text-[#C026D3]'
        },
        {
          title: 'Comptes & Sécurité',
          icon: '🔒',
          desc: 'Gestion des rôles utilisateurs, sauvegardes et accès API.',
          bgClass: 'bg-emerald-100 text-emerald-700'
        }
      ],
      faqList: [
        {
          question: 'Comment créer et structurer un nouveau projet dans GiraafFlow ?',
          answer: 'Depuis la page Projets ou le Tableau de bord, utilisez le formulaire "Nouveau projet". Renseignez le nom, la description et le type. Vous pourrez ensuite y ajouter des étapes/lots avec dates de début et de fin.',
          category: 'Premiers pas',
          open: true
        },
        {
          question: 'Comment fonctionne le tableau Kanban ?',
          answer: 'Le Kanban organise visuellement tous vos projets selon 4 statuts : Brouillon, En cours, En révision et Terminé. Vous pouvez changer le statut d\'un projet directement depuis sa carte pour le faire avancer dans le flux.',
          category: 'Gestion & Kanban',
          open: false
        },
        {
          question: 'Comment connecter l\'assistant IA GiraafChat ?',
          answer: 'Dans la section Comptes, configurez vos intégrations API pour synchroniser automatiquement vos flux avec GiraafChat (chat.giraafpark.com).',
          category: 'Écosystème & IA',
          open: false
        },
        {
          question: 'Mes données et mes projets sont-ils sauvegardés en temps réel ?',
          answer: 'Oui, chaque action de création, mise à jour de lot ou changement de statut est persistée immédiatement sur la base de données sécurisée.',
          category: 'Comptes & Sécurité',
          open: false
        }
      ]
    }
  },
  computed: {
    filteredFaq() {
      if (!this.searchQuery.trim()) return this.faqList
      const q = this.searchQuery.toLowerCase()
      return this.faqList.filter(
        (item) =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q) ||
          item.category.toLowerCase().includes(q)
      )
    }
  },
  methods: {
    toggleFaq(item) {
      item.open = !item.open
    },
    filterByCategory(categoryTitle) {
      this.searchQuery = categoryTitle
    }
  }
}
</script>
